import type {
  AmcSipCalculatorContent,
  AmcSipCalculatorPerTypeContent,
  AmcSipCalculatorResultsSummary,
  AmcSipCalculatorSliderConfig,
  AmcSipCalculatorTableColumns,
  AmcSipCalculatorVisualization,
} from "./AMCSipCalculatorContent.interface";
import {
  DEFAULT_INVESTMENT_SLIDER_MARKERS,
  DEFAULT_INTEREST_RATE_SLIDER_MARKERS,
  DEFAULT_TENURE_SLIDER_MARKERS,
} from "./AMCSipCalculatorConstants";
import type { SipCalculatorPerTypeProps } from "./SipCalculatorTypes";
import type { SipInvestmentType } from "./SipCalculatorCalculations";
import type {
  Aemcontent,
  INavItem,
  IReturnCalculator,
} from "../../interfaces/IAMCContent.interface";

const DEFAULT_SIP_CALCULATOR_SECTION_ID = "sip-calculator";
const DEFAULT_SIP_CALCULATOR_NAV_TITLE = "Calculator";

/** Subset of AEM content used to resolve ReturnCalculator (list may be partial at runtime). */
export type AemContentWithCalculator = {
  calculator?: IReturnCalculator;
  rightSideComponentList?: Aemcontent["rightSideComponentList"] | Record<string, unknown>;
};

type SliderLike = Record<string, unknown> & {
  minValue?: string | number;
  minValuel?: string | number;
};

const normalizeSliderConfig = (slider?: SliderLike): SliderLike | undefined => {
  if (!slider || typeof slider !== "object") return undefined;

  const { minValuel, ...rest } = slider;
  if (minValuel !== undefined && rest.minValue === undefined) {
    return { ...rest, minValue: minValuel };
  }

  return slider;
};

const normalizeVisualization = (
  visualization?: Record<string, unknown>
): Record<string, unknown> | undefined => {
  if (!visualization || typeof visualization !== "object") return undefined;

  const normalizedVisualization = { ...visualization };
  const tableTabLabel =
    normalizedVisualization.tableTabLabel ?? normalizedVisualization["tableTabLabel "];

  if (tableTabLabel !== undefined) {
    normalizedVisualization.tableTabLabel = tableTabLabel;
  }

  delete normalizedVisualization["tableTabLabel "];
  return normalizedVisualization;
};

const normalizePerTypeContent = (
  perType?: AmcSipCalculatorPerTypeContent
): AmcSipCalculatorPerTypeContent | undefined => {
  if (!perType) return undefined;

  return {
    ...perType,
    investmentAmountSlider: normalizeSliderConfig(
      perType.investmentAmountSlider as SliderLike | undefined
    ) as typeof perType.investmentAmountSlider,
    tenureSlider: normalizeSliderConfig(perType.tenureSlider as SliderLike | undefined) as
      typeof perType.tenureSlider | undefined,
    expectedReturnSlider: normalizeSliderConfig(
      perType.expectedReturnSlider as SliderLike | undefined
    ) as typeof perType.expectedReturnSlider,
    visualization: normalizeVisualization(
      perType.visualization as Record<string, unknown> | undefined
    ) as typeof perType.visualization,
  };
};

const normalizeReturnCalculatorPayload = (data: IReturnCalculator): IReturnCalculator => ({
  ...data,
  monthlySip: normalizePerTypeContent(data.monthlySip),
  lumpsum: normalizePerTypeContent(data.lumpsum),
});

export const getReturnCalculatorFromList = (
  list?: Aemcontent["rightSideComponentList"] | Record<string, unknown> | null
): IReturnCalculator | undefined => {
  if (!list) return undefined;

  // Check for ReturnCalculator key
  const direct = (list as Record<string, unknown>).ReturnCalculator;
  if (direct && typeof direct === "object") {
    return direct as IReturnCalculator;
  }

  // Check for calculator key (used in content API)
  const calculator = (list as Record<string, unknown>).calculator;
  if (calculator && typeof calculator === "object") {
    return calculator as IReturnCalculator;
  }

  const match = Object.values(list as Record<string, unknown>).find(
    (item) =>
      item &&
      typeof item === "object" &&
      (item as IReturnCalculator).componentTitle === "ReturnCalculator"
  );

  return match as IReturnCalculator | undefined;
};

/** Resolves ReturnCalculator from AEM headless shape (`calculator` node or legacy right-side list). */
export const getReturnCalculatorFromAemContent = (
  aemContent?: AemContentWithCalculator | null
): IReturnCalculator | undefined => {
  if (!aemContent) return undefined;

  const { calculator } = aemContent;
  if (calculator && typeof calculator === "object") {
    return calculator;
  }

  return getReturnCalculatorFromList(aemContent.rightSideComponentList);
};

export const mapReturnCalculatorToSipCalculatorContent = (
  data?: IReturnCalculator | null
): AmcSipCalculatorContent | undefined => {
  if (!data) return undefined;

  const normalized = normalizeReturnCalculatorPayload(data);
  const content = { ...normalized } as Record<string, unknown>;
  delete content.componentPosition;
  delete content.componentLink;
  delete content.componentTitle;
  delete content.showOnWeb;
  delete content.showOnApp;

  return content as AmcSipCalculatorContent;
};

export const isShowOnWebEnabled = (showOnWeb?: string): boolean => showOnWeb !== "false";

export const resolveReturnCalculatorSectionId = (
  returnCalculator?: IReturnCalculator | null
): string => returnCalculator?.componentLink ?? DEFAULT_SIP_CALCULATOR_SECTION_ID;

export const buildReturnCalculatorNavItem = (
  aemContent?: AemContentWithCalculator | null
): INavItem => {
  const returnCalculator = getReturnCalculatorFromAemContent(aemContent);

  if (returnCalculator?.componentLink) {
    return {
      navigationId: returnCalculator.componentLink,
      navigationTitleText: returnCalculator.title ?? DEFAULT_SIP_CALCULATOR_NAV_TITLE,
    };
  }

  return {
    navigationId: DEFAULT_SIP_CALCULATOR_SECTION_ID,
    navigationTitleText: DEFAULT_SIP_CALCULATOR_NAV_TITLE,
  };
};

/** Keys used for sticky nav that belong to ReturnCalculator (avoid duplicate entries). */
export const isReturnCalculatorNavEntry = (
  component: { componentTitle?: string; componentLink?: string } | null | undefined,
  sipNavId: string
): boolean =>
  component?.componentTitle === "ReturnCalculator" || component?.componentLink === sipNavId;

const DEFAULT_INVESTMENT_MIN = DEFAULT_INVESTMENT_SLIDER_MARKERS[0];
const DEFAULT_INVESTMENT_MAX = DEFAULT_INVESTMENT_SLIDER_MARKERS[1];
const DEFAULT_TENURE_MIN = DEFAULT_TENURE_SLIDER_MARKERS[0];
const DEFAULT_TENURE_MAX = DEFAULT_TENURE_SLIDER_MARKERS[1];
const DEFAULT_RATE_MIN = DEFAULT_INTEREST_RATE_SLIDER_MARKERS[0];
const DEFAULT_RATE_MAX = DEFAULT_INTEREST_RATE_SLIDER_MARKERS[1];

const FALLBACK_INITIAL_INVESTMENT = 25000;
const FALLBACK_INITIAL_TENURE = 10;
const FALLBACK_INITIAL_RATE = 12;

export const parseCmsNumber = (value?: string | number, fallback?: number): number | undefined => {
  if (value === undefined || value === null || value === "") return fallback;

  const parsed = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));

  return Number.isFinite(parsed) ? parsed : fallback;
};

export const mapDefaultInvestmentType = (cmsType?: string): SipInvestmentType =>
  cmsType === "lumpsum" ? "lumpsum" : "sip";

interface MappedSliderConfig {
  min: number;
  max: number;
  default: number;
  step: number;
  markers: number[];
}

export const mapSliderToConfig = (
  slider?: AmcSipCalculatorSliderConfig,
  fallbacks?: { min?: number; max?: number; default?: number; step?: number }
): MappedSliderConfig => {
  const min =
    parseCmsNumber(slider?.minValue ?? slider?.minValuel, fallbacks?.min) ?? fallbacks?.min ?? 0;
  const max = parseCmsNumber(slider?.maxValue, fallbacks?.max) ?? fallbacks?.max ?? min;
  const step = parseCmsNumber(slider?.step, fallbacks?.step) ?? fallbacks?.step ?? 1;
  const defaultValue =
    parseCmsNumber(slider?.defaultValue, fallbacks?.default) ?? fallbacks?.default ?? min;

  return {
    min,
    max,
    default: defaultValue,
    step,
    markers: [min, max],
  };
};

const mapGrowthSuffix = (growthText?: string): string | undefined => {
  if (!growthText) return undefined;

  return growthText
    .replace(/\{growthPercent\}%?\s*/i, "")
    .replace(/\{tenure\}/g, "{years}")
    .trim();
};

type SipCalculatorLabels = NonNullable<SipCalculatorPerTypeProps["labels"]>;

const setLabelIfPresent = (
  labels: SipCalculatorLabels,
  key: keyof SipCalculatorLabels,
  value?: string
): void => {
  if (value) labels[key] = value;
};

const mapBasePerTypeLabels = (
  perType: AmcSipCalculatorPerTypeContent,
  type: SipInvestmentType
): Partial<SipCalculatorLabels> => {
  const labels: SipCalculatorLabels = {};
  const investmentKey = type === "sip" ? "monthlyInvestment" : "lumpsumInvestment";

  setLabelIfPresent(labels, investmentKey, perType.investmentLabel);
  setLabelIfPresent(labels, "tenureYears", perType.tenureLabel);
  setLabelIfPresent(labels, "expectedReturn", perType.expectedReturnLabel);

  return labels;
};

const mapVisualizationLabels = (
  viz?: AmcSipCalculatorVisualization
): Partial<SipCalculatorLabels> => {
  if (!viz) return {};

  const labels: SipCalculatorLabels = {};

  setLabelIfPresent(labels, "chartTab", viz.chartTabLabel);
  setLabelIfPresent(labels, "tableTab", viz.tableTabLabel ?? viz["tableTabLabel "]);
  setLabelIfPresent(labels, "chartTitle", viz.title);
  setLabelIfPresent(labels, "donutEstimatedReturns", viz.estimatedReturnsLegend);
  setLabelIfPresent(labels, "donutInvestedAmount", viz.investedAmountLabel);
  setLabelIfPresent(labels, "donutTotalValue", viz.totalValueLegend);

  return labels;
};

const mapTableColumnLabels = (
  table?: AmcSipCalculatorTableColumns
): Partial<SipCalculatorLabels> => {
  if (!table) return {};

  const labels: SipCalculatorLabels = {};

  setLabelIfPresent(labels, "tableYears", table.yearsLabel);
  setLabelIfPresent(labels, "tableTotalAmount", table.totalAmountLabel);
  setLabelIfPresent(labels, "tableYearlyInvestment", table.yearlyInvestmentLabel);
  setLabelIfPresent(labels, "tableInterestEarned", table.interestEarnedLabel);

  return labels;
};

const mapSummaryLabels = (
  summary?: AmcSipCalculatorResultsSummary
): Partial<SipCalculatorLabels> => {
  if (!summary) return {};

  const labels: SipCalculatorLabels = {};

  setLabelIfPresent(labels, "summaryTotalValue", summary.totalValueLabel);
  setLabelIfPresent(labels, "summaryEstimatedReturns", summary.estimatedReturnsLabel);
  setLabelIfPresent(labels, "summaryInvestedAmount", summary.investedAmountLabel);
  setLabelIfPresent(labels, "summaryGrowthSuffix", mapGrowthSuffix(summary.growthText));

  return labels;
};

const mapPerTypeLabels = (
  perType: AmcSipCalculatorPerTypeContent | undefined,
  type: SipInvestmentType
): Partial<SipCalculatorPerTypeProps["labels"]> | undefined => {
  if (!perType) return undefined;

  const labels = {
    ...mapBasePerTypeLabels(perType, type),
    ...mapVisualizationLabels(perType.visualization as AmcSipCalculatorVisualization | undefined),
    ...mapTableColumnLabels(perType.tableColumns as AmcSipCalculatorTableColumns | undefined),
    ...mapSummaryLabels(perType.resultsSummary as AmcSipCalculatorResultsSummary | undefined),
  };

  return Object.keys(labels).length > 0 ? labels : undefined;
};

export const mapPerTypeContentToProps = (
  perType: AmcSipCalculatorPerTypeContent | undefined,
  type: SipInvestmentType
): SipCalculatorPerTypeProps | undefined => {
  if (!perType) return undefined;

  const investment = mapSliderToConfig(perType.investmentAmountSlider, {
    min: DEFAULT_INVESTMENT_MIN,
    max: DEFAULT_INVESTMENT_MAX,
    default: FALLBACK_INITIAL_INVESTMENT,
    step: 100,
  });
  const tenure = mapSliderToConfig(perType.tenureSlider, {
    min: DEFAULT_TENURE_MIN,
    max: DEFAULT_TENURE_MAX,
    default: FALLBACK_INITIAL_TENURE,
    step: 1,
  });
  const rate = mapSliderToConfig(perType.expectedReturnSlider, {
    min: DEFAULT_RATE_MIN,
    max: DEFAULT_RATE_MAX,
    default: FALLBACK_INITIAL_RATE,
    step: 1,
  });
  const labels = mapPerTypeLabels(perType, type);

  return {
    initialInvestment: investment.default,
    initialTenureYears: tenure.default,
    initialInterestRate: rate.default,
    investmentMin: investment.min,
    investmentMax: investment.max,
    investmentStep: investment.step,
    investmentSliderMarkers: investment.markers,
    tenureMin: tenure.min,
    tenureMax: tenure.max,
    tenureStep: tenure.step,
    tenureSliderMarkers: tenure.markers,
    interestRateMin: rate.min,
    interestRateMax: rate.max,
    interestRateStep: rate.step,
    interestRateSliderMarkers: rate.markers,
    ...(labels ? { labels } : {}),
  };
};

export interface MappedSipCalculatorSymbols {
  rupeeSymbol?: string;
  percentSymbol?: string;
  tenureSuffix?: string;
}

export type MappedSipCalculatorProps = SipCalculatorPerTypeProps & {
  title?: string;
  defaultInvestmentType?: SipInvestmentType;
  labels?: SipCalculatorPerTypeProps["labels"];
  symbols?: MappedSipCalculatorSymbols;
  perTypeConfig?: Partial<Record<SipInvestmentType, SipCalculatorPerTypeProps>>;
};

/** Maps AEM calculator content to SipCalculator props (ignores `scaleLabels`). */
export const mapContentToSipCalculatorProps = (
  content?: AmcSipCalculatorContent | null
): MappedSipCalculatorProps => {
  if (!content) return {};

  const defaultInvestmentType = mapDefaultInvestmentType(content.defaultInvestmentType);
  const sipConfig = mapPerTypeContentToProps(content.monthlySip, "sip");
  const lumpsumConfig = mapPerTypeContentToProps(content.lumpsum, "lumpsum");

  const perTypeConfig: Partial<Record<SipInvestmentType, SipCalculatorPerTypeProps>> = {};
  if (sipConfig) perTypeConfig.sip = sipConfig;
  if (lumpsumConfig) perTypeConfig.lumpsum = lumpsumConfig;

  const defaultTabConfig = perTypeConfig[defaultInvestmentType];

  const labels: NonNullable<SipCalculatorPerTypeProps["labels"]> = {};
  if (content.investmentTypeLabel) labels.investmentType = content.investmentTypeLabel;
  const investmentAmountLabel = content.investmentAmountLabel ?? content.lumpsum?.investmentLabel;
  if (investmentAmountLabel) labels.investmentAmount = investmentAmountLabel;
  if (content.monthlySip?.tabLabel) labels.investmentTypeSip = content.monthlySip.tabLabel;
  if (content.lumpsum?.tabLabel) labels.investmentTypeLumpsum = content.lumpsum.tabLabel;

  const { labels: defaultTabLabels, ...defaultTabProps } = defaultTabConfig ?? {};
  const mergedLabels =
    Object.keys(labels).length > 0 || defaultTabLabels
      ? { ...labels, ...defaultTabLabels }
      : undefined;

  const symbols: MappedSipCalculatorSymbols = {};
  if (content.rupeeSymbol) symbols.rupeeSymbol = content.rupeeSymbol;
  if (content.percentSymbol) symbols.percentSymbol = content.percentSymbol;
  if (content.tenureSuffix) symbols.tenureSuffix = content.tenureSuffix;

  return {
    title: content.title,
    defaultInvestmentType,
    ...(mergedLabels ? { labels: mergedLabels } : {}),
    ...(Object.keys(symbols).length > 0 ? { symbols } : {}),
    ...(Object.keys(perTypeConfig).length > 0 ? { perTypeConfig } : {}),
    ...defaultTabProps,
  };
};
